/* Editing Review Workspace — Feb-2026.
 *
 * Route: /qa/review/:rid  and  /editor/review/:rid
 *
 * Shared collaborative surface used by BOTH the QA reviewer
 * (quality@crownnrose.com) and the assigned Editor. The manuscript
 * author can also open the same URL — they'll see their own view
 * with accept/decline controls on each suggestion.
 *
 * Layout: two-column
 *   ┌────────────────────────────────────┬───────────────────────┐
 *   │ Chapter reader (scrollable)        │ Notes side-panel      │
 *   │ • select text → floating toolbar   │ • anchored comments   │
 *   │ • accepted segments turn green     │ • suggested rewrites  │
 *   │ • approval stamps at manuscript top│ • Approve / Publish   │
 *   └────────────────────────────────────┴───────────────────────┘
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ChevronLeft, ChevronRight, MessageSquarePlus, Highlighter,
  CheckCircle2, XCircle, ShieldCheck, Download, Send, RefreshCw, Stamp, Eye, X,
  Minus, Plus, Bold as BoldIcon,
} from 'lucide-react';
import PublishingStylePanel from '../components/panels/PublishingStylePanel';
import MatterEditor         from '../components/panels/MatterEditor';
import { DEFAULT_MANUSCRIPT_SETTINGS } from '../components/panels/editor/ManuscriptToolbar';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax  = axios.create({ baseURL: API, withCredentials: true });

const STATUS_TONE = {
  pending:  'bg-amber-100 text-amber-900',
  accepted: 'bg-emerald-100 text-emerald-900',
  declined: 'bg-rose-100 text-rose-900',
};

function ApprovalBadges({ approvals }) {
  if (!approvals?.length) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
         data-testid="workspace-approvals">
      <ShieldCheck className="h-4 w-4 text-emerald-700" />
      {approvals.map((a) => (
        <Badge key={a.id || a.reviewer_kind}
               className="bg-emerald-600 text-white uppercase tracking-wider text-[10px]"
               data-testid={`approval-${a.reviewer_kind}`}>
          {a.reviewer_kind} Approved · {(a.decided_at || '').slice(0, 10)} · {a.reviewer_name}
        </Badge>
      ))}
    </div>
  );
}

/** Render chapter body with inline highlights over anchor_text matches. */
function ChapterBody({ content, suggestions, activeSid, onAnchorClick, onMouseUp, readerRef, fontSizePx, bold }) {
  const nodes = useMemo(() => {
    const text = content || '';
    // Sort suggestions by first occurrence in the text so overlapping
    // spans render in a deterministic order.
    const ranges = [];
    suggestions.forEach((s) => {
      if (!s.anchor_text) return;
      let idx = 0;
      while (idx < text.length) {
        const found = text.indexOf(s.anchor_text, idx);
        if (found < 0) break;
        ranges.push({ start: found, end: found + s.anchor_text.length, sug: s });
        idx = found + s.anchor_text.length;
        break; // only highlight first occurrence per suggestion
      }
    });
    ranges.sort((a, b) => a.start - b.start);
    // Merge non-overlapping ranges; if overlap, keep the earlier one.
    const merged = [];
    let cursor = 0;
    ranges.forEach((r) => {
      if (r.start >= cursor) {
        merged.push(r);
        cursor = r.end;
      }
    });
    // Emit segments.
    const out = [];
    let pos = 0;
    merged.forEach((r, i) => {
      if (r.start > pos) out.push({ kind: 'plain', text: text.slice(pos, r.start), key: `p${i}` });
      const tone = r.sug.status === 'accepted'
                     ? 'bg-emerald-200/70'
                     : r.sug.status === 'declined'
                       ? 'bg-rose-100 line-through'
                       : 'bg-amber-200/70';
      const active = r.sug.id === activeSid ? 'ring-2 ring-primary rounded' : '';
      out.push({
        kind: 'mark', text: text.slice(r.start, r.end),
        cls: `${tone} ${active} cursor-pointer px-0.5`,
        sid: r.sug.id, key: `m${i}`,
      });
      pos = r.end;
    });
    if (pos < text.length) out.push({ kind: 'plain', text: text.slice(pos), key: 'tail' });
    return out;
  }, [content, suggestions, activeSid]);

  return (
    <div ref={readerRef} onMouseUp={onMouseUp}
         data-testid="workspace-reader-body"
         className="max-h-[68vh] overflow-y-auto whitespace-pre-wrap p-5 leading-relaxed text-foreground selection:bg-primary/25"
         style={{
           fontFamily: "'EB Garamond', Georgia, serif",
           fontSize: `${fontSizePx || 17}px`,
           fontWeight: bold ? 700 : 400,
         }}>
      {nodes.map((n) => n.kind === 'plain'
        ? <span key={n.key}>{n.text}</span>
        : <mark key={n.key} className={n.cls}
                onClick={() => onAnchorClick?.(n.sid)}
                data-testid={`highlight-${n.sid}`}>{n.text}</mark>)}
    </div>
  );
}

const MIN_TYPO_SIZE = 12;
const MAX_TYPO_SIZE = 32;
const clampTypoSize = (n) => Math.max(MIN_TYPO_SIZE, Math.min(MAX_TYPO_SIZE, Math.round(Number(n) || 14)));

/** Small typography toolbar shared across the three manuscript readers —
 * reads/writes the SAME `project.manuscript_settings` the main writer's
 * editor uses (font_size_px, font_weight), so the look stays consistent
 * no matter which surface you open. Non-authors (reviewers) still get a
 * live control, but their changes are local-only (no write permission
 * on someone else's project) — they just don't persist across reloads. */
function TypographyToolbar({ fontSizePx, bold, onSize, onBold }) {
  return (
    <div className="flex items-center gap-1 border-b border-border px-3 py-1.5" data-testid="workspace-typo-toolbar">
      <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
              onClick={() => onSize(clampTypoSize(fontSizePx - 1))}
              data-testid="workspace-font-size-down" title="Decrease text size">
        <Minus className="h-3 w-3" />
      </Button>
      <span className="min-w-[30px] text-center font-mono text-[10.5px] tabular-nums text-muted-foreground"
            data-testid="workspace-font-size-display">
        {clampTypoSize(fontSizePx)}px
      </span>
      <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
              onClick={() => onSize(clampTypoSize(fontSizePx + 1))}
              data-testid="workspace-font-size-up" title="Increase text size">
        <Plus className="h-3 w-3" />
      </Button>
      <span className="mx-1 h-4 w-px bg-border" />
      <Button variant="ghost" size="sm"
              className={`h-6 w-6 p-0 ${bold ? 'text-primary bg-primary/10' : ''}`}
              onClick={() => onBold(!bold)}
              data-testid="workspace-bold-toggle" title="Toggle bold text" aria-pressed={bold}>
        <BoldIcon className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}


export default function EditingReviewWorkspacePage() {
  const { rid } = useParams();
  const [state,    setState]    = useState({ loading: true, error: '' });
  const [data,     setData]     = useState(null);
  const [chIdx,    setChIdx]    = useState(0);
  const [selection,setSelection]= useState(null);
  const [busy,     setBusy]     = useState(false);
  const [activeSid,setActiveSid]= useState(null);
  const [approveKind, setApproveKind] = useState('editor');
  const [notes,    setNotes]    = useState('');
  const [showPub,  setShowPub]  = useState(false);
  const [showMatter, setShowMatter] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [typo, setTypo] = useState({
    font_size_px: DEFAULT_MANUSCRIPT_SETTINGS.font_size_px,
    font_weight: DEFAULT_MANUSCRIPT_SETTINGS.font_weight,
  });
  const typoSaveTimer = useRef(null);
  const readerRef = useRef(null);

  const load = useCallback(async () => {
    setState({ loading: true, error: '' });
    try {
      const r = await ax.get(`/editing/requests/${rid}/workspace`);
      setData(r.data);
      const ms = r.data?.project?.manuscript_settings;
      setTypo({
        font_size_px: ms?.font_size_px || DEFAULT_MANUSCRIPT_SETTINGS.font_size_px,
        font_weight:  ms?.font_weight  || DEFAULT_MANUSCRIPT_SETTINGS.font_weight,
      });
      // Default reviewer_kind based on viewer email pattern.
      if (r.data?.viewer?.is_reviewer) {
        setApproveKind(
          (r.data?.viewer?.user_id || '').toLowerCase().includes('quality@')
            ? 'qa' : 'editor'
        );
      }
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || e.message });
      return;
    }
    setState({ loading: false, error: '' });
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  const chapters    = data?.chapters || [];
  const chapter     = chapters[chIdx];
  const suggestions = data?.suggestions || [];
  const chSug       = suggestions.filter((s) => s.chapter_id === chapter?.id);
  const approvals   = data?.approvals || [];
  const isReviewer  = !!data?.viewer?.is_reviewer;
  const isAuthor    = !!data?.viewer?.is_author;

  // Shared typography — mirrors the writer's manuscript settings
  // (font_size_px / font_weight) so the reader pane stays consistent
  // with the main editor. Only the author's changes persist back to
  // the project (reviewers get a live, local-only adjustment — they
  // don't have write access to someone else's project).
  const patchTypo = (delta) => {
    const next = { ...typo, ...delta };
    setTypo(next);
    if (!isAuthor || !data?.project?.id) return;
    if (typoSaveTimer.current) clearTimeout(typoSaveTimer.current);
    typoSaveTimer.current = setTimeout(() => {
      ax.put(`/projects/${data.project.id}`, {
        manuscript_settings: { ...(data.project.manuscript_settings || {}), ...next },
      }).catch(() => { /* non-fatal */ });
    }, 500);
  };

  const onMouseUp = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !readerRef.current?.contains(sel.anchorNode)) {
      setSelection(null);
      return;
    }

    const text = (sel.toString() || '').trim();
    if (!text || !chapter) { setSelection(null); return; }
    setSelection({ text: text.slice(0, 2000), chapter_id: chapter.id });
  };

  const createSuggestion = async (kind) => {
    if (!selection || !isReviewer) return;
    const note = kind === 'comment'
      ? window.prompt('Note for the author (comment):')
      : null;
    const rewrite = kind === 'edit'
      ? window.prompt('Suggested rewrite (replaces the selected text):')
      : null;
    if (kind === 'comment' && !(note || '').trim()) return;
    if (kind === 'edit'    && !(rewrite || '').trim()) return;
    setBusy(true);
    try {
      await ax.post(`/editing/requests/${rid}/suggestions`, {
        chapter_id:    selection.chapter_id,
        kind,
        anchor_text:   selection.text,
        proposed_text: rewrite || undefined,
        note:          note    || undefined,
      });
      toast.success('Suggestion posted to the author.');
      window.getSelection?.().removeAllRanges();
      setSelection(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suggestion failed.');
    } finally { setBusy(false); }
  };

  const resolveSuggestion = async (sid, action) => {
    if (!isAuthor) return;
    setBusy(true);
    try {
      await ax.post(`/suggestions/${sid}/${action}`, {});
      toast.success(action === 'accept'
        ? 'Change accepted and applied.'
        : 'Suggestion declined.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally { setBusy(false); }
  };

  const stamp = async () => {
    if (!isReviewer) return;
    setBusy(true);
    try {
      await ax.post(`/editing/requests/${rid}/approve`, {
        reviewer_kind: approveKind, notes: notes || '',
      });
      toast.success(`${approveKind.toUpperCase()} Approval stamp applied.`);
      setNotes('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed.');
    } finally { setBusy(false); }
  };

  const downloadEpub = async () => {
    setBusy(true);
    try {
      const r = await ax.get(`/editing/requests/${rid}/export/epub`, { responseType: 'blob' });
      const url  = URL.createObjectURL(new Blob([r.data], { type: 'application/epub+zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data?.project?.name || 'book'}.epub`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'EPUB export failed.');
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await ax.get(`/editing/requests/${rid}/export/kdp-pdf`, { responseType: 'blob' });
      const url  = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data?.project?.name || 'book'}-KDP.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KDP PDF export failed.');
    } finally { setBusy(false); }
  };

  const publishFinal = async () => {
    if (!isAuthor) { toast.error('Only the author can publish.'); return; }
    const price = window.prompt('Retail price in cents (e.g. 499 for $4.99)', '499');
    if (!price) return;
    const genre = window.prompt('Genre slug (e.g. "romance", "scifi")', 'romance');
    if (!genre) return;
    setBusy(true);
    try {
      const r = await ax.post(`/editing/requests/${rid}/publish-final`, {
        price_cents: parseInt(price, 10) || 499,
        genre_slug:  genre.trim(),
        back_copy:   data?.project?.back_copy || '',
        cover_front_url: data?.project?.cover_url || '',
        publishing_style: data?.publishing_style,
      });
      toast.success(`Published to bookstore · ${r.data.book_id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.detail
                    || e?.response?.data?.detail
                    || 'Publish failed.');
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading manuscript…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center" data-testid="workspace-error">
        <div className="text-[15px] font-semibold text-red-800">Cannot open review</div>
        <p className="mt-2 text-[13px] text-muted-foreground">{state.error}</p>
        <Link to="/qa" className="mt-4 inline-block text-[12px] text-primary underline">
          Back to QA Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-4" data-testid="editing-workspace">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-muted-foreground">
            Collaborative Review · {data?.request?.id?.slice(-8)}
          </div>
          <h1 className="font-serif text-[22px] leading-tight">
            {data?.project?.name || 'Untitled manuscript'}
          </h1>
          <div className="text-[11.5px] text-muted-foreground">
            by {data?.project?.author || data?.author?.name || data?.author?.email}
            {' · '}{data?.request?.word_count?.toLocaleString?.()} words
            {' · '}status <b>{data?.request?.status}</b>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={load} disabled={busy}
                  data-testid="workspace-refresh-btn">
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={downloadEpub} disabled={busy}
                  data-testid="workspace-download-epub-btn">
            <Download className="mr-1 h-3.5 w-3.5" /> EPUB
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPdf} disabled={busy}
                  data-testid="workspace-download-pdf-btn">
            <Download className="mr-1 h-3.5 w-3.5" /> KDP PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowPreview(true)}
                  data-testid="workspace-preview-btn">
            <Eye className="mr-1 h-3.5 w-3.5" /> Preview finished book
          </Button>
          {isAuthor && (
            <Button size="sm" onClick={() => setShowPub((v) => !v)}
                    data-testid="workspace-publishing-style-btn">
              Fonts &amp; Layout
            </Button>
          )}
          {(isAuthor || isReviewer) && (
            <Button size="sm" variant="outline"
                    onClick={() => setShowMatter((v) => !v)}
                    data-testid="workspace-matter-btn">
              Front &amp; Back Matter
            </Button>
          )}
          {isAuthor && approvals.length > 0 && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={publishFinal} disabled={busy}
                    data-testid="workspace-publish-btn">
              <Send className="mr-1 h-3.5 w-3.5" /> Publish to Store
            </Button>
          )}
        </div>
      </header>

      <ApprovalBadges approvals={approvals} />

      {showPub && isAuthor && (
        <PublishingStylePanel projectId={data?.project?.id}
                              onSaved={load}
                              onClose={() => setShowPub(false)} />
      )}
      {showMatter && (isAuthor || isReviewer) && (
        <div className="mb-4">
          <MatterEditor projectId={data?.project?.id}
                        onSaved={load}
                        onClose={() => setShowMatter(false)} />
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
             data-testid="workspace-preview-modal"
             onClick={() => setShowPreview(false)}>
          <div className="relative flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <header className="flex items-center justify-between border-b border-border px-4 py-2">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.24em] text-muted-foreground">
                  Preview finished book · typography + matter + approvals
                </div>
                <div className="text-[13px] font-semibold">
                  {data?.project?.name || 'Manuscript'}
                </div>
              </div>
              <button onClick={() => setShowPreview(false)}
                      className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                      data-testid="workspace-preview-close">
                <X className="h-4 w-4" />
              </button>
            </header>
            <iframe title="Book preview"
                    src={`${API}/editing/requests/${rid}/preview-book`}
                    className="flex-1 border-0 bg-white"
                    data-testid="workspace-preview-iframe" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Reader */}
        <section className="lg:col-span-3 rounded-md border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <div>
              <div className="text-[13px] font-semibold">
                Ch. {chIdx + 1} · {chapter?.title || 'Untitled'}
              </div>
              <div className="text-[10.5px] text-muted-foreground">
                {(chapter?.words || 0).toLocaleString()} words
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline"
                      onClick={() => setChIdx((i) => Math.max(0, i - 1))}
                      disabled={chIdx <= 0}
                      data-testid="workspace-ch-prev">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <div className="px-2 text-[11px] text-muted-foreground">
                {chIdx + 1} / {chapters.length}
              </div>
              <Button size="sm" variant="outline"
                      onClick={() => setChIdx((i) => Math.min(chapters.length - 1, i + 1))}
                      disabled={chIdx >= chapters.length - 1}
                      data-testid="workspace-ch-next">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <TypographyToolbar
            fontSizePx={typo.font_size_px}
            bold={typo.font_weight === 'bold'}
            onSize={(v) => patchTypo({ font_size_px: v })}
            onBold={(v) => patchTypo({ font_weight: v ? 'bold' : 'normal' })}
          />
          <ChapterBody content={chapter?.content || ''}
                       suggestions={chSug}
                       activeSid={activeSid}
                       onAnchorClick={setActiveSid}
                       onMouseUp={onMouseUp}
                       readerRef={readerRef}
                       fontSizePx={typo.font_size_px}
                       bold={typo.font_weight === 'bold'} />
          {selection && isReviewer && (
            <div className="sticky bottom-0 flex items-center gap-2 border-t border-border bg-background/95 p-2 shadow"
                 data-testid="workspace-selection-bar">
              <span className="line-clamp-1 flex-1 text-[11px] italic text-muted-foreground">
                &ldquo;{selection.text}&rdquo;
              </span>
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => createSuggestion('comment')}
                      data-testid="workspace-suggest-comment-btn">
                <MessageSquarePlus className="mr-1 h-3 w-3" /> Comment
              </Button>
              <Button size="sm" disabled={busy}
                      onClick={() => createSuggestion('edit')}
                      data-testid="workspace-suggest-edit-btn">
                <Highlighter className="mr-1 h-3 w-3" /> Suggest rewrite
              </Button>
            </div>
          )}
        </section>

        {/* Notes side-panel */}
        <aside className="lg:col-span-2 space-y-3">
          {/* Reviewer approve card */}
          {isReviewer && (
            <div className="rounded-md border border-border bg-card p-3"
                 data-testid="workspace-approve-card">
              <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold">
                <Stamp className="h-3.5 w-3.5" /> Reviewer approval
              </div>
              <div className="mb-2 flex gap-1">
                {['editor', 'qa'].map((k) => (
                  <button key={k}
                          onClick={() => setApproveKind(k)}
                          data-testid={`workspace-approve-kind-${k}`}
                          className={`rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase ${
                            approveKind === k
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background hover:bg-secondary'}`}>
                    {k}
                  </button>
                ))}
              </div>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional notes for the author…"
                        data-testid="workspace-approve-notes"
                        className="mb-2" />
              <Button size="sm" onClick={stamp} disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="workspace-approve-btn">
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                Stamp {approveKind.toUpperCase()} Approved
              </Button>
              <p className="mt-2 text-[10.5px] text-muted-foreground">
                Highlight text in the reader and click <b>Comment</b> or <b>Suggest rewrite</b>
                to leave anchored notes.
              </p>
            </div>
          )}

          {/* Suggestions list */}
          <div className="rounded-md border border-border bg-card p-3"
               data-testid="workspace-suggestions-panel">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[13px] font-semibold">Notes on this chapter</div>
              <Badge variant="outline" className="text-[10px]">{chSug.length}</Badge>
            </div>
            {chSug.length === 0 ? (
              <div className="text-[11px] text-muted-foreground">
                No notes yet on this chapter.
              </div>
            ) : (
              <ul className="max-h-[52vh] space-y-2 overflow-y-auto">
                {chSug.map((s) => (
                  <li key={s.id}
                      onClick={() => setActiveSid(s.id)}
                      className={`cursor-pointer rounded border p-2 text-[11.5px] ${
                        activeSid === s.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-secondary/40'}`}
                      data-testid={`workspace-sug-${s.id}`}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <Badge className={`uppercase text-[9px] ${STATUS_TONE[s.status] || ''}`}>
                          {s.status}
                        </Badge>
                        <Badge variant="outline" className="text-[9px] uppercase">
                          {s.reviewer_kind || s.kind}
                        </Badge>
                      </div>
                      <span className="text-[10px] text-muted-foreground">{s.author_name}</span>
                    </div>
                    <div className="italic text-muted-foreground">
                      &ldquo;{(s.anchor_text || '').slice(0, 220)}&rdquo;
                    </div>
                    {s.proposed_text && (
                      <div className="mt-1 rounded bg-emerald-50 px-1.5 py-1">
                        → {s.proposed_text.slice(0, 240)}
                      </div>
                    )}
                    {s.note && (
                      <div className="mt-1 text-muted-foreground">{s.note.slice(0, 240)}</div>
                    )}
                    {isAuthor && s.status === 'pending' && (
                      <div className="mt-1.5 flex gap-1">
                        <Button size="sm" className="h-6 bg-emerald-600 hover:bg-emerald-700"
                                onClick={(e) => { e.stopPropagation(); resolveSuggestion(s.id, 'accept'); }}
                                disabled={busy}
                                data-testid={`workspace-sug-accept-${s.id}`}>
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Accept
                        </Button>
                        <Button size="sm" variant="outline" className="h-6"
                                onClick={(e) => { e.stopPropagation(); resolveSuggestion(s.id, 'decline'); }}
                                disabled={busy}
                                data-testid={`workspace-sug-decline-${s.id}`}>
                          <XCircle className="mr-1 h-3 w-3" /> Decline
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
